import { Flex, Heading, SegmentedControl } from '@radix-ui/themes';
import { SessionRecapHelper } from 'classes/helpers/session-recap.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { ChartTooltip } from 'components/sections/analytics/session-hits/recap/visualizations/chart-tooltip';
import { ChartDots } from 'components/sections/analytics/session-hits/recap/visualizations/dots';
import {
  FAIR_TERRITORY_PATH,
  FIELD_HEIGHT,
  FIELD_HOME,
  FIELD_UNITS_PER_FT,
  FIELD_WIDTH,
  FieldIllustration,
  INFIELD_PATH,
} from 'components/sections/analytics/session-hits/recap/visualizations/field-illustration';
import { useRecapHover } from 'components/sections/analytics/session-hits/recap/visualizations/use-recap-hover';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import {
  RecapFieldMode,
  RecapZoneFilterKind,
  SPRAY_CENTER_DEG,
  SprayDepth,
  SprayDirection,
} from 'enums/session-recap/enums';
import { t } from 'i18next';
import { IChartDot } from 'interfaces/session-recap/i-chart-dot';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { DEG_TO_RAD } from 'lib_ts/classes/math.utilities';
import { HitterSide, MAX_FOUL_H_LAUNCH_DEG } from 'lib_ts/enums/hitters.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useMemo } from 'react';
import useMiscStore from 'stores/use-misc-store';

// room around the illustration for balls hit beyond the fence or into foul territory
const VIEW = {
  x: -16,
  y: -40,
  width: FIELD_WIDTH + 32,
  height: FIELD_HEIGHT + 48,
};

const DOT_RADIUS = 5.5;
const LABEL_SIZE = 13;

/** far enough that fair-territory and infield clips, not this radius, shape the zone */
const WEDGE_OUTER_FT = 460;

/** feet from home (+x towards 1B, +y towards CF) to illustration units */
const toSvg = (x: number, y: number) => ({
  x: FIELD_HOME.x + x * FIELD_UNITS_PER_FT,
  y: FIELD_HOME.y - y * FIELD_UNITS_PER_FT,
});

const polarFt = (deg: number, r: number) => {
  const rad = Math.PI / 2 - deg * DEG_TO_RAD;
  return toSvg(Math.cos(rad) * r, Math.sin(rad) * r);
};

const wedgePath = (fromDeg: number, toDeg: number, r1: number, r2: number) => {
  const steps = Math.max(2, Math.ceil(Math.abs(toDeg - fromDeg) / 3));
  const outer: string[] = [];
  const inner: string[] = [];

  for (let i = 0; i <= steps; i++) {
    const deg = fromDeg + ((toDeg - fromDeg) * i) / steps;
    const o = polarFt(deg, r2);
    const n = polarFt(deg, r1);
    outer.push(`${o.x.toFixed(1)} ${o.y.toFixed(1)}`);
    inner.unshift(`${n.x.toFixed(1)} ${n.y.toFixed(1)}`);
  }

  return `M ${outer.join(' L ')} L ${inner.join(' L ')} Z`;
};

const directionRange = (
  direction: SprayDirection,
  side: HitterSide
): [number, number] => {
  // -ve angles are towards 3B, i.e. the pull side for a RHH
  const pullTo3B = side !== HitterSide.LHH;
  const thirdSide: [number, number] = [
    -MAX_FOUL_H_LAUNCH_DEG,
    -SPRAY_CENTER_DEG,
  ];
  const firstSide: [number, number] = [SPRAY_CENTER_DEG, MAX_FOUL_H_LAUNCH_DEG];

  switch (direction) {
    case SprayDirection.Pull:
      return pullTo3B ? thirdSide : firstSide;
    case SprayDirection.Oppo:
      return pullTo3B ? firstSide : thirdSide;
    case SprayDirection.Center:
    default:
      return [-SPRAY_CENTER_DEG, SPRAY_CENTER_DEG];
  }
};

const SprayZonesOverlay = (props: {
  records: ISessionRecapHit[];
  side: HitterSide;
  infieldClipID: string;
  fairClipID: string;
  maskID: string;
  selected: { direction: SprayDirection; depth: SprayDepth } | null;
  onSelect: (direction: SprayDirection, depth: SprayDepth) => void;
}) => {
  const cells = useMemo(
    () => SessionRecapHelper.getSprayZones(props.records, props.side),
    [props.records, props.side]
  );

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  // relative to an even split across the six zones
  const scale = { min: 0, baseline: 1 / cells.length, max: 0.35 };

  const geometry = (c: (typeof cells)[number]) => {
    const [from, to] = directionRange(c.direction, props.side);

    return {
      d: wedgePath(
        from,
        to,
        c.depth === SprayDepth.Infield ? -30 : 0,
        WEDGE_OUTER_FT
      ),
      label: polarFt(
        (from + to) / 2,
        c.depth === SprayDepth.Infield ? 95 : 270
      ),
      selected:
        props.selected?.direction === c.direction &&
        props.selected.depth === c.depth,
    };
  };

  const zone = (c: (typeof cells)[number]) => {
    const { d, selected } = geometry(c);

    return (
      <g
        key={`${c.direction}-${c.depth}`}
        className="recap-spray-zone-group"
        data-selected={selected || undefined}
      >
        {!isDarkMode && c.count > 0 && (
          <path d={d} className="recap-zone-backing" />
        )}
        <path
          d={d}
          fill={SessionRecapHelper.getDivergingColor(c.share, scale)}
          fillOpacity={
            c.count
              ? SessionRecapHelper.getDivergingOpacity(
                  c.share,
                  scale,
                  true,
                  !isDarkMode
                )
              : 0.04
          }
          className="recap-spray-zone"
          pointerEvents="all"
          role="button"
          aria-label={t('hitters.recap-filter-zone').toString()}
          aria-pressed={selected}
          onClick={() => props.onSelect(c.direction, c.depth)}
        />
      </g>
    );
  };

  const zoneLabel = (c: (typeof cells)[number]) => {
    if (!c.count) {
      return null;
    }

    const { label, selected } = geometry(c);

    return (
      <text
        key={`${c.direction}-${c.depth}`}
        x={label.x}
        y={label.y}
        className="recap-zone-label"
        data-selected={selected || undefined}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={LABEL_SIZE}
      >
        {`${Math.round(c.share * 100)}%`}
      </text>
    );
  };

  const infield = cells.filter((c) => c.depth === SprayDepth.Infield);
  const outfield = cells.filter((c) => c.depth === SprayDepth.Outfield);

  return (
    <g
      className="recap-spray-zones"
      data-active={props.selected ? true : undefined}
    >
      {/* infield is painted after the fair clip, so a click on the dirt selects infield */}
      <g clipPath={`url(#${props.fairClipID})`} mask={`url(#${props.maskID})`}>
        {outfield.map(zone)}
      </g>
      <g clipPath={`url(#${props.infieldClipID})`}>{infield.map(zone)}</g>
      {!isDarkMode && (
        <g className="recap-field-lines" pointerEvents="none">
          <path d={FAIR_TERRITORY_PATH} />
          <path d={INFIELD_PATH} />
        </g>
      )}
      <g pointerEvents="none">{cells.map(zoneLabel)}</g>
    </g>
  );
};

interface IProps {
  /** Every pitch in the current recap filters. Spray zones use this. */
  records: ISessionRecapHit[];
  /** Legend- and zone-filtered pitches drawn as dots. */
  shots: ISessionRecapHit[];
  hitterSide: HitterSide;
  highlightKey?: string | null;
  /** locks the chart to one mode and hides the spray/zones toggle */
  mode?: RecapFieldMode;
  /** keeps clip and mask ids unique when more than one field is mounted */
  instanceID?: string;
}

export const RecapFieldView = (props: IProps) => {
  const colorByID = useAnalyticsStore((s) => s.colorByID);
  const storedMode = useAnalyticsStore((s) => s.fieldMode);
  const zoneFilter = useAnalyticsStore((s) => s.zoneFilter);

  const fieldMode = props.mode ?? storedMode;
  const clipID = `${props.instanceID ?? 'recap'}-infield`;
  const fairClipID = `${props.instanceID ?? 'recap'}-fair`;
  const maskID = `${props.instanceID ?? 'recap'}-outfield`;
  const selectedSpray =
    zoneFilter?.kind === RecapZoneFilterKind.Spray ? zoneFilter : null;

  const hover = useRecapHover();

  const dots = useMemo(
    () =>
      props.shots.reduce((acc, r) => {
        const landing = SessionRecapHelper.getLandingFt(r.hit);

        if (!landing) {
          return acc;
        }

        const p = toSvg(landing.x, landing.y);

        const dotColor = SessionRecapHelper.getColorForDot(r, colorByID);

        acc.push({
          id: r.hit._id,
          x: Math.min(
            VIEW.x + VIEW.width - DOT_RADIUS,
            Math.max(VIEW.x + DOT_RADIUS, p.x)
          ),
          y: Math.min(
            VIEW.y + VIEW.height - DOT_RADIUS,
            Math.max(VIEW.y + DOT_RADIUS, p.y)
          ),
          color: dotColor.color,
          muted: dotColor.muted,
          legendKey: SessionRecapHelper.getLegendKey(r, colorByID),
        });
        return acc;
      }, [] as IChartDot[]),
    [props.shots, colorByID]
  );

  const isZones = fieldMode === RecapFieldMode.Zones;
  const title = props.mode
    ? `${t('hitters.recap-field-view')} · ${t(
        props.mode === RecapFieldMode.Zones
          ? 'hitters.recap-zones'
          : 'hitters.recap-spray'
      )}`
    : t('hitters.recap-field-view');

  const visibleDots = useMemo(() => {
    if (!isZones || zoneFilter) {
      return dots;
    }

    return dots.filter(
      (d) => d.id === hover.hoveredHitID || d.id === hover.selectedHitID
    );
  }, [isZones, zoneFilter, dots, hover.hoveredHitID, hover.selectedHitID]);

  const hoveredRecord =
    hover.pointer && hover.hoveredHitID
      ? props.records.find((r) => r.hit._id === hover.hoveredHitID)
      : undefined;

  return (
    <ErrorBoundary identifier="RecapFieldView">
      <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
        <Flex
          justify="between"
          align="center"
          gap={RADIX.FLEX.GAP.SM}
          className="recap-chart-header"
        >
          <Heading size={RADIX.HEADING.SIZE.SM}>{title}</Heading>

          {!props.mode && (
            <SegmentedControl.Root
              size="1"
              value={fieldMode}
              onValueChange={(v) =>
                useAnalyticsStore.setState({ fieldMode: v as RecapFieldMode })
              }
            >
              <SegmentedControl.Item value={RecapFieldMode.Spray}>
                {t('hitters.recap-spray')}
              </SegmentedControl.Item>
              <SegmentedControl.Item value={RecapFieldMode.Zones}>
                {t('hitters.recap-zones')}
              </SegmentedControl.Item>
            </SegmentedControl.Root>
          )}
        </Flex>

        <div className="recap-chart" onPointerLeave={hover.clear}>
          <svg
            viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.width} ${VIEW.height}`}
            role="img"
            aria-label={title.toString()}
          >
            <defs>
              <clipPath id={clipID}>
                <path d={INFIELD_PATH} />
              </clipPath>
              <clipPath id={fairClipID}>
                <path d={FAIR_TERRITORY_PATH} />
              </clipPath>
              <mask id={maskID} maskUnits="userSpaceOnUse">
                <path d={FAIR_TERRITORY_PATH} fill="white" />
                <path d={INFIELD_PATH} fill="black" />
              </mask>
            </defs>

            <FieldIllustration />

            {isZones && (
              <SprayZonesOverlay
                records={props.records}
                side={props.hitterSide}
                infieldClipID={clipID}
                fairClipID={fairClipID}
                maskID={maskID}
                selected={selectedSpray}
                onSelect={(direction, depth) => {
                  const current = useAnalyticsStore.getState().zoneFilter;
                  const same =
                    current?.kind === RecapZoneFilterKind.Spray &&
                    current.direction === direction &&
                    current.depth === depth;

                  useAnalyticsStore.setState({
                    zoneFilter: same
                      ? null
                      : {
                          kind: RecapZoneFilterKind.Spray,
                          direction,
                          depth,
                        },
                  });
                }}
              />
            )}

            <ChartDots
              dots={visibleDots}
              hoveredID={hover.hoveredHitID}
              selectedID={hover.selectedHitID}
              onEnter={hover.onEnter}
              onLeave={hover.onLeave}
              onSelect={hover.onSelect}
              highlightKey={props.highlightKey}
            />
          </svg>
        </div>
      </Flex>

      {hover.pointer && hoveredRecord && (
        <ChartTooltip record={hoveredRecord} pointer={hover.pointer} />
      )}
    </ErrorBoundary>
  );
};
